import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '../services/notificationService';
import type { AppNotification } from '../types/notifications';

const notificationQueryKeys = {
  list: (userId?: string | null) => ['notifications', userId ?? 'anonymous'] as const,
  unreadCount: (userId?: string | null) => ['notifications', 'unread-count', userId ?? 'anonymous'] as const,
};

const updateUnreadCountCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string | null | undefined,
  updater: (current: number) => number,
) => {
  if (!userId) return;
  queryClient.setQueryData<number>(notificationQueryKeys.unreadCount(userId), (current) =>
    updater(Number(current ?? 0)),
  );
};

export const useNotifications = (userId?: string | null) =>
  useQuery({
    queryKey: notificationQueryKeys.list(userId),
    enabled: Boolean(userId),
    queryFn: async ({ signal }) => {
      if (!userId) return [];
      return notificationService.getNotifications(userId, { signal });
    },
    staleTime: 30_000,
    refetchInterval: userId ? 60_000 : false,
  });

export const useUnreadNotificationCount = (userId?: string | null) =>
  useQuery({
    queryKey: notificationQueryKeys.unreadCount(userId),
    enabled: Boolean(userId),
    queryFn: async () => {
      if (!userId) return 0;
      return notificationService.getUnreadNotificationCount(userId);
    },
    staleTime: 30_000,
    refetchInterval: userId ? 60_000 : false,
  });

export const useMarkNotificationAsRead = (userId?: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      await notificationService.markNotificationAsRead(notificationId);
      return notificationId;
    },
    onSuccess: (notificationId) => {
      if (!userId) return;

      let decremented = false;
      queryClient.setQueryData<AppNotification[]>(notificationQueryKeys.list(userId), (current) => {
        const items = Array.isArray(current) ? current : [];
        return items.map((item) => {
          if (item.id !== notificationId) return item;
          if (!item.read) decremented = true;
          return { ...item, read: true };
        });
      });

      if (decremented) {
        updateUnreadCountCache(queryClient, userId, (current) => Math.max(0, current - 1));
      }
    },
    onSettled: () => {
      if (!userId) return;
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.list(userId) });
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.unreadCount(userId) });
    },
  });
};

export const useMarkAllNotificationsAsRead = (userId?: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!userId) return;
      await notificationService.markAllNotificationsAsRead(userId);
    },
    onSuccess: () => {
      if (!userId) return;
      queryClient.setQueryData<AppNotification[]>(notificationQueryKeys.list(userId), (current) =>
        Array.isArray(current) ? current.map((item) => ({ ...item, read: true })) : current,
      );
      queryClient.setQueryData<number>(notificationQueryKeys.unreadCount(userId), 0);
    },
    onSettled: () => {
      if (!userId) return;
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.list(userId) });
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.unreadCount(userId) });
    },
  });
};

export const useDeleteNotification = (userId?: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notification: AppNotification) => {
      await notificationService.deleteNotification(notification.id);
      return notification;
    },
    onSuccess: (notification) => {
      if (!userId) return;
      queryClient.setQueryData<AppNotification[]>(notificationQueryKeys.list(userId), (current) =>
        Array.isArray(current) ? current.filter((item) => item.id !== notification.id) : current,
      );
      if (!notification.read) {
        updateUnreadCountCache(queryClient, userId, (current) => Math.max(0, current - 1));
      }
    },
    onSettled: () => {
      if (!userId) return;
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.list(userId) });
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.unreadCount(userId) });
    },
  });
};

export const useClearAllNotifications = (userId?: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!userId) return;
      await notificationService.clearAllNotifications(userId);
    },
    onSuccess: () => {
      if (!userId) return;
      queryClient.setQueryData<AppNotification[]>(notificationQueryKeys.list(userId), []);
      queryClient.setQueryData<number>(notificationQueryKeys.unreadCount(userId), 0);
    },
    onSettled: () => {
      if (!userId) return;
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.list(userId) });
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.unreadCount(userId) });
    },
  });
};

export { notificationQueryKeys };
